import { __rest, __awaiter } from 'tslib';
import { jsx, Fragment, jsxs } from 'react/jsx-runtime';
import { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { css, cx } from '@ts-tools/emotion';
import { H3, Body } from '@leafygreen-ui/typography';
import { palette } from '@leafygreen-ui/palette';
import Button, { Variant } from '@leafygreen-ui/button';
import Checkbox from '@leafygreen-ui/checkbox';
import Icon from '@leafygreen-ui/icon';

const defaultFeatureFlagsContext = {
    featureFlagsGlobal: {},
    featureFlagsUser: {},
    displayFlags: "Atlas",
    setFeatureFlags: () => null,
    setDisplayFlags: () => null,
};
const FeatureFlagsContext = createContext(defaultFeatureFlagsContext);
const useFeatureFlagsContext = () => {
    return useContext(FeatureFlagsContext);
};
const FeatureFlagsProvider = ({ flagsGlobal = {}, flagsAtlas = {}, flagsLocal = {}, children, }) => {
    const [featureFlagsAtlas, setFeatureFlagsAtlas] = useState(flagsAtlas);
    const [featureFlagsLocal, setFeatureFlagsLocal] = useState(flagsLocal);
    const [displayFlags, setDisplayFlags] = useState(Object.entries(featureFlagsLocal).length > 0 ? "Local" : "Atlas"); /* Use LocalStorage flags by default (if they exist) */
    useEffect(() => {
        setFeatureFlagsLocal(flagsLocal);
    }, [flagsLocal]);
    useEffect(() => {
        const atlasLength = Object.keys(featureFlagsAtlas).length;
        const localLength = Object.keys(featureFlagsLocal).length;
        const hasAtlasNotLocal = atlasLength > 0 && localLength === 0;
        const hasLocalNotAtlas = localLength > 0 && atlasLength === 0;
        const hasNeither = atlasLength === 0 && localLength === 0;
        if (hasAtlasNotLocal || hasNeither) {
            setDisplayFlags("Atlas");
        }
        if (hasLocalNotAtlas) {
            setDisplayFlags("Local");
        }
    }, [featureFlagsAtlas, featureFlagsLocal]);
    return (jsx(FeatureFlagsContext.Provider, Object.assign({ value: {
            featureFlagsGlobal: displayFlags === "Local" ? {} : flagsGlobal,
            featureFlagsUser: displayFlags === "Local" ? featureFlagsLocal : featureFlagsAtlas,
            displayFlags: displayFlags,
            setFeatureFlags: displayFlags === "Local"
                ? setFeatureFlagsLocal
                : setFeatureFlagsAtlas,
            setDisplayFlags,
        } }, { children: children })));
};
const useFeatureFlag = (name) => {
    const { featureFlagsGlobal, featureFlagsUser } = useFeatureFlagsContext();
    /**
     * User flags will ALWAYS supersede global flags.
     * For this reason, make sure to avoid duplicate
     * flag names unless done intentionallly!
     * */
    if (name in featureFlagsUser)
        return featureFlagsUser[name];
    return !!featureFlagsGlobal[name];
};
const BaseFeature = ({ name, children, status = "show" }) => {
    const [mounted, setMounted] = useState(false);
    const hasFeature = useFeatureFlag(name);
    const shouldHide = status !== "show" ? hasFeature : !hasFeature;
    const emptyDivTag = (jsx("div", { className: "feature-flag", style: { width: 0, height: 0, margin: 0, padding: 0 } }));
    /**
     * Checking whether or not the component is mounted
     * is crucial to prevent a Next.js hydration mismatch error
     * otherwise the component will be rendered on the server...
     * */
    useEffect(() => {
        setMounted(true);
    }, []);
    if (!mounted) {
        return emptyDivTag;
    }
    if (shouldHide) {
        return emptyDivTag;
    }
    return (jsxs(Fragment, { children: [emptyDivTag, children] }));
};
/**
 * This neat trick uses the Javascript Proxy API
 * to dynamically create subcomponents per flag;
 * Instead of manually using the "name" prop, you can
 * now create <Feature.FLAG> component on the fly!
 * */
const Feature = new Proxy(BaseFeature, {
    get(target, prop) {
        if (typeof prop === "string" && !(prop in target)) {
            return (_a) => {
                var { children } = _a, props = __rest(_a, ["children"]);
                return (jsx(BaseFeature, Object.assign({}, props, { name: prop }, { children: children })));
            };
        }
        return jsx(Fragment, {});
    },
});

const hasFeatureFlag = (flag, session) => {
    if (session.flags == null) {
        return false;
    }
    if (flag in session.flags.user) {
        return session.flags.user[flag] === true;
    }
    if (flag in session.flags.global) {
        return session.flags.global[flag] === true;
    }
    return false;
};

const containerStyle = css `
  position: fixed;
  bottom: 1rem;
  left: 1rem;
  z-index: 100;
  padding: 1rem;

  background: white;
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.2),
    0 4px 6px -4px rgb(0 0 0 / 0.2);
`;
const containerMinimizedStyle = css `
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: 100%;
  cursor: pointer;
`;
const containerHasActiveFlags = css `
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.2),
    0 4px 6px -4px rgb(0 0 0 / 0.2),
    0 0 0 0.2rem ${palette.yellow.base};
`;
const hover = css `
  transition:
    transform 0.3s ease,
    box-shadow 0.2s ease-in-out;

  &:hover {
    transform: scale(1.1);
    box-shadow:
      0 10px 15px -3px rgb(0 0 0 / 0.2),
      0 4px 6px -4px rgb(0 0 0 / 0.2),
      0 0 0 0.2rem ${palette.green.base};
  }
`;
const popIn = css `
  animation: pop-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @keyframes pop-in {
    0% {
      transform: scale(1.2);
    }
    100% {
      transform: scale(1);
    }
  }
`;
const slideIn = css `
  animation: slide-in 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);

  @keyframes slide-in {
    0% {
      transform: translateY(100px);
    }
    100% {
      transform: translateY(0);
    }
  }
`;
const swapStyle = css `
  color: ${palette.gray.dark1};
  cursor: pointer;
  transition: all 0.8s ease;
  &:hover {
    transform: rotate(180deg);
  }
`;
const flagsListStyle = css `
  overflow-y: scroll;
  margin: 0.5rem 0;
  margin-block-end: 0;
  padding-inline-start: 0;

  & > li:nth-child(even) {
    background: ${palette.gray.light3};
  }
`;
const flagsItemStyle = css `
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.25rem;
`;
const activeSymbol = css `
  display: flex;
  width: 0.5rem;
  height: 0.5rem;
  padding: 0.5rem;

  border-radius: 100%;
`;
const deleteStyle = css `
  padding: 0.1rem;
  border-radius: 100%;
  cursor: pointer;

  color: ${palette.gray.base};
  background: transparent;

  transition: all 0.2s;

  &:hover {
    color: ${palette.white};
    background: ${palette.red.base};

    transform: scale(1.2);
  }
`;
const FeatureFlagsToggle = ({ saveFlagsLocal, saveFlagsAtlas, }) => {
    const { featureFlagsGlobal, featureFlagsUser, displayFlags, setFeatureFlags, setDisplayFlags, } = useFeatureFlagsContext();
    const [expanded, setExpanded] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(false);
    const [numberFeaturesOnPage, setNumberFeaturesOnPage] = useState(0);
    useEffect(() => {
        if (typeof document === "undefined") {
            return;
        }
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                if (mutation.type === "childList" || mutation.type === "attributes") {
                    setNumberFeaturesOnPage(document.querySelectorAll(".feature-flag").length);
                }
            });
        });
        const config = { attributes: true, childList: true, subtree: true };
        const targetNode = document.querySelector("#root");
        if (targetNode === null) {
            return;
        }
        observer.observe(targetNode, config);
        return () => observer.disconnect();
    }, []);
    const toggleExpand = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setExpanded((e) => !e);
    };
    const toggleFlag = useCallback((flag) => {
        const flags = Object.assign({}, featureFlagsUser);
        flags[flag] = !flags[flag];
        setFeatureFlags(flags);
    }, [featureFlagsUser]);
    const deleteFlag = useCallback((flag) => {
        const flags = Object.assign({}, featureFlagsUser);
        delete flags[flag];
        setFeatureFlags(flags);
    }, [featureFlagsUser]);
    const handleSaveFlagsLocal = () => {
        saveFlagsLocal(featureFlagsUser);
    };
    const handleSaveFlagsAtlas = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setError(false);
            setLoading(true);
            const response = yield saveFlagsAtlas(featureFlagsUser);
            setFeatureFlags(response);
        }
        catch (e) {
            console.error(e);
            setError(true);
        }
        finally {
            setLoading(false);
        }
    });
    if (!expanded) {
        return (jsx("div", Object.assign({ className: cx(containerStyle, containerMinimizedStyle, popIn, hover, numberFeaturesOnPage > 0 && containerHasActiveFlags), onClick: toggleExpand }, { children: jsx(H3, Object.assign({ style: { color: palette.black } }, { children: numberFeaturesOnPage > 0 ? numberFeaturesOnPage : "🛠️" })) })));
    }
    return (jsxs("div", Object.assign({ className: cx(containerStyle, slideIn), style: {
            minWidth: "360px",
            borderRadius: "1rem",
        } }, { children: [jsxs("div", Object.assign({ style: { display: "flex", justifyContent: "space-between" } }, { children: [jsxs("span", { children: [jsx(H3, Object.assign({ style: { color: palette.gray.dark3 } }, { children: "Feature Flags" })), jsxs("span", Object.assign({ style: { display: "flex", alignItems: "center", gap: "0.2rem" } }, { children: [jsxs(Body, Object.assign({ style: { color: palette.gray.dark3 } }, { children: ["Developer Preview: ", jsx("span", { children: displayFlags })] })), jsx(Icon, { glyph: "Refresh", className: swapStyle, onClick: () => setDisplayFlags(displayFlags === "Atlas" ? "Local" : "Atlas") })] }))] }), jsx(Icon, { glyph: "ChevronDown", style: { color: palette.gray.light1, cursor: "pointer" }, onClick: toggleExpand })] })), jsxs("ul", Object.assign({ className: flagsListStyle }, { children: [Object.entries(featureFlagsGlobal).map(([key, value], index) => (jsxs("li", Object.assign({ className: flagsItemStyle }, { children: [jsx("span", Object.assign({ style: {
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                } }, { children: jsx("span", Object.assign({ style: { color: palette.gray.dark2 } }, { children: key })) })), jsx("span", { className: activeSymbol, style: {
                                    background: value ? palette.green.base : palette.red.light1,
                                } })] }), index))), !!Object.entries(featureFlagsGlobal).length && jsx("hr", {}), Object.entries(featureFlagsUser).map(([key, value], index) => (jsxs("li", Object.assign({ className: flagsItemStyle }, { children: [jsxs("span", Object.assign({ style: { display: "flex", alignItems: "center" } }, { children: [jsx(Checkbox, { id: key, "aria-label": `${!value ? "Enable" : "Disable"} feature flag "${key}"`, darkMode: true, checked: value, onChange: () => toggleFlag(key) }), jsx("label", Object.assign({ htmlFor: key, style: { cursor: "pointer", color: palette.gray.dark2 } }, { children: key }))] })), jsx(Icon, { glyph: "X", className: deleteStyle, onClick: () => deleteFlag(key) })] }), index))), jsx("li", Object.assign({ className: flagsItemStyle }, { children: jsx("input", { "aria-label": "Add a new user flag input", type: "text", onKeyDown: (e) => {
                                if (e.key === "Enter") {
                                    const flags = Object.assign({}, featureFlagsUser);
                                    flags[e.currentTarget.value] = true;
                                    setFeatureFlags(flags);
                                    e.currentTarget.value = "";
                                }
                            }, placeholder: "Enter flag name here...", style: { width: "100%" } }) }))] })), jsxs("span", Object.assign({ style: { float: "right", display: "flex", gap: "1rem" } }, { children: [displayFlags === "Local" && (jsx(Button, Object.assign({ "aria-label": "Save user feature flag configuration to local storage", onClick: handleSaveFlagsLocal, variant: Variant.PrimaryOutline, disabled: loading }, { children: "Save Local" }))), displayFlags === "Atlas" && (jsx(Button, Object.assign({ "aria-label": "Save user feature flag configuration to Atlas", onClick: handleSaveFlagsAtlas, variant: error ? Variant.Danger : Variant.BaseGreen, disabled: loading }, { children: "Save Atlas" })))] }))] })));
};

export { Feature, FeatureFlagsProvider, FeatureFlagsToggle, hasFeatureFlag, useFeatureFlag };
//# sourceMappingURL=index.js.map
